import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import styles from './AddExternalFinancialTransactionModal.module.css';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { toast } from 'react-toastify';
import { ExternalFinancialTransaction, FinancialTransactionOption } from '../types';

interface AddExternalFinancialTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sources: FinancialTransactionOption[];
  categories: FinancialTransactionOption[];
  selectedTransaction?: ExternalFinancialTransaction | null;
  duplicateFromTransaction?: ExternalFinancialTransaction | null;
  lockedSource?: string;
  lockedSourceLabel?: string;
  titleOverride?: string;
}

type FieldErrors = {
  date?: string;
  description?: string;
  amount?: string;
  finance_source?: string;
  direction?: string;
};

type DirectionOption = { key: string; label: string; sign: 1 | -1; lockedCategory?: string };

const EXTERNAL_DIRECTIONS: DirectionOption[] = [
  { key: 'income', label: 'Income', sign: 1 },
  { key: 'expense', label: 'Expense', sign: -1 },
];

const EXPENSE_CLAIM_DIRECTIONS: DirectionOption[] = [
  { key: 'received_from', label: 'Payment received from Tini Express', sign: 1, lockedCategory: 'inter_account_transfer' },
  { key: 'received_for', label: 'Payment received for Tini Express', sign: 1 },
  { key: 'personal_expense', label: 'Personal expense with business account', sign: 1, lockedCategory: 'director_loan' },
  { key: 'personal_expense_repayment', label: 'Pay back personal expense to Tini Express', sign: -1, lockedCategory: 'director_loan' },
  { key: 'made_for', label: 'Payment made for Tini Express', sign: -1 },
  { key: 'made_to', label: 'Payment made to Tini Express', sign: -1, lockedCategory: 'inter_account_transfer' },
];

// Some expense-claim directions imply a fixed category — money moving between Tini
// and the director (inter-account transfer), or a personal spend on a Tini account
// (and paying it back) booked as a director loan. These lock the Category field; the
// note explains why. Booking both the spend and the repayment as Director Loan means
// borrowing and repaying in the same period net to zero.
const LOCKED_CATEGORY_NOTES: Record<string, string> = {
  inter_account_transfer:
    'Locked to “Inter Account Transfer” because this direction represents money moving between Tini Express and the personal account.',
  director_loan:
    'Locked to “Director Loan” because a personal expense paid with a Tini Express account — and paying it back — is tracked as a director loan, so a borrow and repayment cancel out.',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Expense claims are routinely backdated, so the date is deliberately left blank there:
// prefilling today's date made it easy to save a claim against the wrong day. Computed on
// demand rather than held in a variable — a wall-clock value in the reset effect's dependency
// array would re-run the effect (wiping the form) on a render that straddles midnight.
const defaultDateFor = (isExpenseClaimFlavour: boolean) => (isExpenseClaimFlavour ? '' : todayIso());

const defaultDirectionFor = (
  options: DirectionOption[],
  rawAmount: string,
  savedCategory: string | null,
): string => {
  const value = parseFloat(rawAmount);
  if (isNaN(value) || value === 0) return '';
  const sign = value > 0 ? 1 : -1;
  const candidates = options.filter(o => o.sign === sign);
  // The direction isn't persisted, so on edit we reconstruct it from the sign and,
  // when the saved category matches a category-locking direction, prefer that one.
  const categoryMatch = savedCategory
    ? candidates.find(o => o.lockedCategory === savedCategory)
    : undefined;
  if (categoryMatch) return categoryMatch.key;
  const passthrough = candidates.find(o => !o.lockedCategory);
  if (passthrough) return passthrough.key;
  return candidates[0]?.key || '';
};

const AddExternalFinancialTransactionModal: React.FC<AddExternalFinancialTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  sources,
  categories,
  selectedTransaction,
  duplicateFromTransaction,
  lockedSource,
  lockedSourceLabel,
  titleOverride,
}) => {
  const isEdit = !!selectedTransaction;
  const isDuplicating = !isEdit && !!duplicateFromTransaction;
  const isExpenseClaimFlavour = !!lockedSource;
  const directionOptions = useMemo(
    () => (isExpenseClaimFlavour ? EXPENSE_CLAIM_DIRECTIONS : EXTERNAL_DIRECTIONS),
    [isExpenseClaimFlavour],
  );

  const [date, setDate] = useState(() => defaultDateFor(isExpenseClaimFlavour));
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [financeSource, setFinanceSource] = useState('');
  const [category, setCategory] = useState('');
  const [direction, setDirection] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const source = selectedTransaction || duplicateFromTransaction;
      if (source) {
        setDate(selectedTransaction ? source.date : defaultDateFor(isExpenseClaimFlavour));
        setDescription(source.description);
        const raw = source.amount || '';
        setAmount(raw.startsWith('-') ? raw.slice(1) : raw);
        setFinanceSource(lockedSource || source.finance_source);
        setCategory(source.category || '');
        setDirection(defaultDirectionFor(directionOptions, source.amount, source.category));
        setNotes(source.notes || '');
      } else {
        setDate(defaultDateFor(isExpenseClaimFlavour));
        setDescription('');
        setAmount('');
        setFinanceSource(lockedSource || '');
        setCategory('');
        setDirection('');
        setNotes('');
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, selectedTransaction, duplicateFromTransaction, lockedSource, directionOptions, isExpenseClaimFlavour]);

  const lockedCategory = directionOptions.find(o => o.key === direction)?.lockedCategory;
  const isCategoryLocked = !!lockedCategory;
  const lockedCategoryLabel = lockedCategory
    ? (categories.find(c => c.key === lockedCategory)?.label ?? lockedCategory)
    : undefined;

  const handleDirectionChange = (next: string) => {
    setDirection(next);
    const nextLockedCategory = directionOptions.find(o => o.key === next)?.lockedCategory;
    if (nextLockedCategory) {
      setCategory(nextLockedCategory);
    } else if (category && directionOptions.some(o => o.lockedCategory === category)) {
      // Leaving a category-locking direction — clear the auto-set category.
      setCategory('');
    }
  };

  const validate = (): FieldErrors => {
    const errs: FieldErrors = {};
    if (!date) errs.date = 'Date is required';
    if (!description.trim()) errs.description = 'Description is required';
    const trimmedAmount = amount.trim();
    if (!trimmedAmount) {
      errs.amount = 'Amount is required';
    } else if (isNaN(Number(trimmedAmount))) {
      errs.amount = 'Amount must be a number';
    } else if (Number(trimmedAmount) === 0) {
      errs.amount = 'Amount must not be zero';
    } else if (Number(trimmedAmount) < 0) {
      errs.amount = 'Amount must be positive';
    }
    if (!(lockedSource || financeSource)) errs.finance_source = 'Source is required';
    if (!direction) errs.direction = 'Direction is required';
    return errs;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setIsSubmitting(true);

    const sign = directionOptions.find(o => o.key === direction)?.sign ?? 1;
    const positiveAmount = amount.trim().replace(/^-/, '');
    const signedAmount = sign === -1 ? `-${positiveAmount}` : positiveAmount;

    try {
      const url = isEdit
        ? `/external_financial_transactions/${selectedTransaction!.id}`
        : '/external_financial_transactions';
      const response = await fetchWithAuth(url, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify({
          external_financial_transaction: {
            date,
            description: description.trim(),
            amount: signedAmount,
            finance_source: lockedSource || financeSource,
            category: category || null,
            notes: notes.trim(),
          },
        }),
      });

      if (response.ok) {
        toast.success(isEdit ? 'Transaction updated successfully' : 'Transaction added successfully');
        onSuccess();
        onClose();
      } else {
        const data = await response.json().catch(() => ({}));
        const apiErrors: FieldErrors = {};
        if (data.errors) {
          Object.entries(data.errors).forEach(([key, value]) => {
            const message = Array.isArray(value) ? value[0] : String(value);
            if (key === 'date' || key === 'description' || key === 'amount' || key === 'finance_source') {
              apiErrors[key] = message;
            }
          });
        }
        setErrors(apiErrors);
        if (Object.keys(apiErrors).length === 0) {
          toast.error(isEdit ? 'Failed to update transaction' : 'Failed to add transaction');
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const heading = titleOverride
    ? titleOverride
    : isEdit
      ? 'Edit Transaction'
      : 'Add Transaction';

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} className={styles.modalContent}>
      <h2 className="text-2xl font-bold mb-4">{heading}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="externalTxnDate" className="block text-gray-700">Date</label>
          <input
            id="externalTxnDate"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
          {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
        </div>

        <div>
          <label htmlFor="externalTxnDescription" className="block text-gray-700">Description</label>
          <input
            id="externalTxnDescription"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="e.g. Yoco settlement 2026-05-15"
          />
          {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
        </div>

        <fieldset>
          <legend className="block text-gray-700">Direction</legend>
          <div className={`mt-1 ${isExpenseClaimFlavour ? 'space-y-2' : 'flex gap-6'}`}>
            {directionOptions.map((option) => (
              <label key={option.key} className="flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="radio"
                  name="externalTxnDirection"
                  value={option.key}
                  checked={direction === option.key}
                  onChange={(e) => handleDirectionChange(e.target.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
          {isExpenseClaimFlavour && (isEdit || isDuplicating) && (
            <p className="text-xs text-gray-500 mt-1">
              The selected direction is used to determine the sign; the original label is not saved, so re-pick if it doesn&apos;t match what you originally chose.
            </p>
          )}
          {errors.direction && <p className="text-red-500 text-sm mt-1">{errors.direction}</p>}
        </fieldset>

        <div>
          <label htmlFor="externalTxnAmount" className="block text-gray-700">Amount</label>
          <input
            id="externalTxnAmount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder="e.g. 150.00"
          />
          {errors.amount && <p className="text-red-500 text-sm mt-1">{errors.amount}</p>}
        </div>

        <div>
          <label htmlFor="externalTxnSource" className="block text-gray-700">Source</label>
          {lockedSource ? (
            <input
              id="externalTxnSource"
              type="text"
              value={lockedSourceLabel || (sources.find(s => s.key === lockedSource)?.label ?? lockedSource)}
              readOnly
              disabled
              className="w-full px-3 py-2 border rounded bg-gray-100 text-gray-700"
            />
          ) : (
            <select
              id="externalTxnSource"
              value={financeSource}
              onChange={(e) => setFinanceSource(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="">Select a source...</option>
              {sources.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          )}
          {errors.finance_source && <p className="text-red-500 text-sm mt-1">{errors.finance_source}</p>}
        </div>

        <div>
          <label htmlFor="externalTxnCategory" className="block text-gray-700">Category</label>
          <select
            id="externalTxnCategory"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isCategoryLocked}
            className={`w-full px-3 py-2 border rounded ${isCategoryLocked ? 'bg-gray-100 text-gray-700' : ''}`}
          >
            <option value="">Uncategorised</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
            {isCategoryLocked && lockedCategory && !categories.some(c => c.key === lockedCategory) && (
              <option value={lockedCategory}>{lockedCategoryLabel}</option>
            )}
          </select>
          {isCategoryLocked && lockedCategory && (
            <p className="text-xs text-gray-500 mt-1">
              {LOCKED_CATEGORY_NOTES[lockedCategory] ?? `Locked to “${lockedCategoryLabel}” for the selected direction.`}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="externalTxnNotes" className="block text-gray-700">Notes (optional)</label>
          <textarea
            id="externalTxnNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Anything worth remembering about this transaction"
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Transaction')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddExternalFinancialTransactionModal;
